import { useQuery } from '@tanstack/react-query';
import { supabaseClient, supabaseCoreClient } from '../../../utils/supabase';
import type { DynamicRecord } from '../../../types/api';

interface UseDeletedRecordParams {
  tableName: string;
  schemaName: string;
  recordId: number;
  isEnabled: boolean;
}

interface UseDeletedRecordResult {
  wasDeleted: boolean | undefined;
  isLoadingAudit: boolean;
  deletedRecordData: DynamicRecord | null;
}

/**
 * Hook to check if a record was deleted using SECURITY DEFINER function
 * This allows all users to see that a record was deleted, without exposing audit data
 *
 * Also optimistically fetches the deleted record data from the audit table.
 * RLS will only return data for admin users, allowing them to view the record
 * details in a grayed-out state. Non-admins will get null and see the generic message.
 */
export const useDeletedRecord = ({
  tableName,
  schemaName,
  recordId,
  isEnabled
}: UseDeletedRecordParams): UseDeletedRecordResult => {
  // Check if record was deleted (for all users)
  const { data: wasDeleted, isLoading: isLoadingDeleted } = useQuery({
    queryKey: ['deleted-record-check', schemaName, tableName, recordId],
    queryFn: async () => {
      // Call SECURITY DEFINER function to check if record was deleted
      // This bypasses RLS so non-admin users can see deletion status
      // Use supabaseCoreClient since check_record_deleted lives in core_crm schema
      const { data, error } = await supabaseCoreClient.rpc('check_record_deleted', {
        p_schema_name: schemaName,
        p_table_name: tableName,
        p_record_id: recordId
      });

      if (error) {
        console.error('Error checking if record was deleted:', error);
        return false;
      }

      return data === true;
    },
    enabled: isEnabled,
    staleTime: Infinity,
    retry: false
  });

  // Optimistically fetch deleted record data
  // RLS on audit table will only return data for admins
  const { data: deletedRecordData, isLoading: isLoadingData } = useQuery({
    queryKey: ['deleted-record-data', schemaName, tableName, recordId],
    queryFn: async () => {
      const auditTableName = `_${tableName}_audit`;

      // Query the audit table for the DELETE operation
      // RLS will only return data for admin users
      const { data, error } = await (supabaseClient as any)
        .schema(schemaName)
        .from(auditTableName)
        .select('old_data')
        .eq('parent_id', recordId)
        .eq('operation', 'DELETE')
        .order('transaction_time', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching deleted record data:', error);
        return null;
      }

      // old_data contains the full record before deletion
      return data?.old_data || null;
    },
    enabled: isEnabled && wasDeleted === true,
    staleTime: Infinity,
    retry: false
  });

  const isLoadingAudit = isEnabled && (
    isLoadingDeleted ||
    wasDeleted === undefined ||
    (wasDeleted === true && isLoadingData)
  );

  return {
    wasDeleted: wasDeleted,
    isLoadingAudit,
    deletedRecordData: deletedRecordData || null
  };
};
